import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Send } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { VISA_TYPES, COUNTRIES } from "../data/content";
import { Reveal, SectionHeading } from "../lib/motion";
import { t } from "../i18n";

const EMPTY = { name: "", phone: "", email: "", city: "", visa_type: "", country: "", message: "" };

const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries", { ...form, source: "enquiry" });
      setDone(true);
      setForm(EMPTY);
      toast.success("Enquiry submitted successfully. Our team will contact you soon.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquiry" data-testid="enquiry-section" className="relative py-24 lg:py-32 bg-navy overflow-hidden">
      <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-gold/5 blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={t("enquiry.eyebrow")}
          title={t("enquiry.title")}
          description={t("enquiry.desc")}
          testid="enquiry-heading"
        />
        <Reveal className="max-w-4xl mx-auto">
          <div className="glass-card rounded-2xl p-6 sm:p-10 shadow-[0_30px_80px_-30px_rgba(2,6,23,0.9)]">
            {done ? (
              <div className="text-center py-10" data-testid="enquiry-success">
                <CheckCircle2 className="h-14 w-14 text-gold mx-auto mb-5" />
                <h3 className="font-display text-2xl font-bold text-white mb-3">{t("enquiry.successTitle")}</h3>
                <p className="text-slate-400 max-w-md mx-auto text-sm leading-relaxed">
                  {t("enquiry.successText")}
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="enquiry-another-btn"
                  className="mt-8 btn-outline-light rounded-full px-6 py-3 text-xs font-bold tracking-wider font-display"
                >
                  {t("enquiry.another")}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="quick-enquiry-form">
                <input required minLength={2} value={form.name} onChange={set("name")} placeholder={t("enquiry.name")} data-testid="enquiry-name-input" className="input-premium" />
                <input required minLength={6} value={form.phone} onChange={set("phone")} placeholder={t("enquiry.phone")} data-testid="enquiry-phone-input" className="input-premium" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder={t("enquiry.email")} data-testid="enquiry-email-input" className="input-premium" />
                <input value={form.city} onChange={set("city")} placeholder={t("enquiry.city")} data-testid="enquiry-city-input" className="input-premium" />
                <select required value={form.visa_type} onChange={set("visa_type")} data-testid="enquiry-visa-select" className="input-premium appearance-none">
                  <option value="" disabled className="bg-navy">{t("enquiry.visaType")}</option>
                  {VISA_TYPES.map((v) => (
                    <option key={v} value={v} className="bg-navy">{v}</option>
                  ))}
                </select>
                <select required value={form.country} onChange={set("country")} data-testid="enquiry-country-select" className="input-premium appearance-none">
                  <option value="" disabled className="bg-navy">{t("enquiry.country")}</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.slug} value={c.name} className="bg-navy">{c.name}</option>
                  ))}
                  <option value="Other" className="bg-navy">Other</option>
                </select>
                <textarea
                  value={form.message}
                  onChange={set("message")}
                  placeholder={t("enquiry.message")}
                  rows={4}
                  data-testid="enquiry-message-input"
                  className="input-premium sm:col-span-2 resize-none"
                />
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="enquiry-submit-btn"
                    className="btn-gold w-full rounded-full py-4 text-sm font-bold tracking-wider font-display flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {loading ? t("enquiry.submitting") : t("enquiry.submit")} <Send className="h-4 w-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default EnquiryForm;
